/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Convert a hex color (e.g. "#7755BE") into Lottie's normalized [r, g, b] triple (0..1).
 */
const hexToNormalizedRgb = (hex: string): [number, number, number] => {
    const value = hex.replace('#', '');
    const r = parseInt(value.slice(0, 2), 16) / 255;
    const g = parseInt(value.slice(2, 4), 16) / 255;
    const b = parseInt(value.slice(4, 6), 16) / 255;
    return [r, g, b];
};

/**
 * Deep-clone a Lottie animation and force every fill/stroke/gradient to a single color,
 * preserving each gradient stop's position and opacity.
 *
 * Handles:
 *  - solid fills / strokes (`fl` / `st`): `c.k` = [r, g, b, a]
 *  - gradient fills / strokes (`gf` / `gs`): `g.k.k` = [pos, r, g, b, ...colorStops, pos, opacity, ...opacityStops]
 */
export const recolorLottie = <T,>(animationData: T, hex: string): T => {
    const [r, g, b] = hexToNormalizedRgb(hex);
    const clone = JSON.parse(JSON.stringify(animationData));

    const recolorGradientStops = (stops: number[], stopCount: number): number[] => {
        const next = [...stops];
        // Color section: `stopCount` entries of [position, r, g, b].
        for (let i = 0; i < stopCount; i++) {
            const offset = i * 4;
            next[offset + 1] = r;
            next[offset + 2] = g;
            next[offset + 3] = b;
        }
        return next;
    };

    const walk = (node: any): void => {
        if (Array.isArray(node)) {
            node.forEach(walk);
            return;
        }
        if (!node || typeof node !== 'object') return;

        // Solid fill / stroke: static color [r, g, b, a].
        if ((node.ty === 'fl' || node.ty === 'st') && node.c?.a === 0 && Array.isArray(node.c.k)) {
            node.c.k[0] = r;
            node.c.k[1] = g;
            node.c.k[2] = b;
        }

        // Gradient fill / stroke: static color+opacity stops.
        if ((node.ty === 'gf' || node.ty === 'gs') && node.g?.k?.a === 0 && Array.isArray(node.g.k.k)) {
            node.g.k.k = recolorGradientStops(node.g.k.k, node.g.p ?? 0);
        }

        Object.values(node).forEach(walk);
    };

    walk(clone);
    return clone;
};
